import { useState } from 'react';
import { produce } from 'immer';
import { Pencil, Tag } from '@hungryroot/icons';
import { TransparentButton, Text } from '@hungryroot/ramen';
import { type PricingConfig } from '@hungryroot/rest/api/get_pricing_config/get';
import { type PromoCodeType } from '@hungryroot/rest/api/v2/promo/[code]/get';
import { type InvalidPromoCode } from '../../lib/validate-promo';
import { PromoCodeInput } from '../PromoCodeInput/PromoCodeInput';
import styles from './PromoCode.module.css';

interface Props {
  effectStr: PricingConfig['promo_effect']['effect_str'];
  code?: PricingConfig['promo_effect']['promos'][number]['code'];
  onSubmit: (value: string) => Promise<void>;
  validate: (value: string) => Promise<PromoCodeType | InvalidPromoCode>;
  testid?: string;
  initialShowInput: boolean;
  onBlur?: () => void;
}

export function PromoCode({
  validate,
  effectStr,
  code,
  onSubmit,
  testid,
  initialShowInput = false,
  onBlur,
}: Props) {
  const [state, setState] = useState<{
    loading: boolean;
    showInput: boolean;
    error?: string;
  }>({
    loading: false,
    showInput: initialShowInput,
  });

  const { error, loading, showInput } = state;

  if (showInput === true) {
    return (
      <PromoCodeInput
        autofocus
        placeholder="Enter promo"
        actionText="Apply"
        error={error}
        loading={loading}
        onCancel={() => {
          // do not dismiss if loading is true
          if (loading === true) {
            return;
          }
          // return to initial state
          onBlur?.();
          setState(
            produce((draft) => {
              draft.showInput = false;
              draft.error = undefined;
            })
          );
        }}
        onSubmit={async (value) => {
          setState(
            produce((draft) => {
              draft.loading = true;
            })
          );

          const validationResult = await validate(value);
          if ('valid' in validationResult && validationResult.valid === false) {
            setState(
              produce((draft) => {
                draft.error = validationResult.message;
                draft.loading = false;
              })
            );
            return;
          }
          // return to initial state
          onBlur?.();
          await onSubmit(value);
          setState(
            produce((draft) => {
              draft.loading = false;
              draft.showInput = false;
            })
          );
        }}
        initialValue={code}
        label="Promo"
      />
    );
  }

  return (
    <div data-testid={`${testid}-promo-code`} className={styles.container}>
      <div className={styles.effect}>
        <Tag />
        <Text>{effectStr}</Text>
      </div>
      <TransparentButton
        onClick={() => {
          setState(
            produce((draft) => {
              draft.showInput = true;
            })
          );
        }}
        className={styles.button}
        ariaLabel="Edit Promo"
      >
        <Pencil />
      </TransparentButton>
    </div>
  );
}
